/**
 * Questions asked of a room's slot board — the availability read's `slots`, admin or guest.
 *
 * The slots themselves come from the API — it decides the opening window and the increment — so
 * nothing here invents a board.
 */

import type { ResourceOperatingHour, ResourceWeekday } from '../types/resource';
import { keyOf, parseKey } from './dates';

/** `Date.getDay()` numbering: 0 is Sunday. */
const DAY_NUMBER: Record<ResourceWeekday, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * The weekdays a room is shut, as `Date.getDay()` numbers, from its weekly
 * hours. A weekday with no entry is shut, as the server reads it.
 */
export const closedWeekdays = (hours: ResourceOperatingHour[]): number[] =>
  (Object.keys(DAY_NUMBER) as ResourceWeekday[])
    .filter((weekday) => {
      const day = hours.find((h) => h.weekday === weekday);
      return !day || day.isClosed;
    })
    .map((weekday) => DAY_NUMBER[weekday]);

/**
 * The first day from `from` (yyyy-mm-dd) the room is open, so a calendar does
 * not open on a closed day. `from` itself when every day is shut.
 */
export function firstOpenDay(from: string, closed: number[]): string {
  const start = parseKey(from);
  for (let i = 0; i < 7; i++) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    if (!closed.includes(day.getDay())) return keyOf(day);
  }
  return from;
}

/** What every slot board has, whether or not it says who holds a taken slot. */
export interface BoardSlot {
  startMinute: number;
  endMinute: number;
  available: boolean;
}

/** The slot covering a given minute mark, if the board has one. */
export const slotAt = <S extends BoardSlot>(slots: S[], minutes: number) =>
  slots.find((s) => minutes >= s.startMinute && minutes < s.endMinute);

/**
 * True when [from, to) is covered by slots and every one of them is free.
 *
 * Only ever an early answer: the server retakes the lock and rechecks occupancy
 * as it writes, so a run that passes here can still lose the race.
 */
export function runAvailable(slots: BoardSlot[], from: number, to: number): boolean {
  const covered = slots.filter((s) => s.startMinute < to && s.endMinute > from);
  const minutes = covered.reduce((n, s) => n + (s.endMinute - s.startMinute), 0);
  return minutes === to - from && covered.every((s) => s.available);
}
